import { useState, useEffect, useCallback } from "react";
import productsService from "../services/productsService";
import { CACHE_CONFIG } from "./useProductsCache";

const usePopularCategories = () => {
  const [popularCategories, setPopularCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cacheSource, setCacheSource] = useState("none");

  // Configuración específica para categorías
  const CATEGORIES_CACHE_CONFIG = {
    STORAGE_KEY: "tattersall-popular-categories-cache",
    METADATA_KEY: "tattersall-popular-categories-metadata",
    TTL: 20 * 60 * 1000, // 20 minutos
    DEFAULT_LIMIT: 6,
  };

  // Función para guardar categorías en sessionStorage
  const saveCategoriesStorage = useCallback((categories) => {
    try {
      if (!categories || categories.length === 0) return;

      const cacheData = {
        categories,
        timestamp: Date.now(),
        version: CACHE_CONFIG.VERSION,
        count: categories.length,
        type: "popular-categories",
      };

      const metadata = {
        lastUpdate: Date.now(),
        version: CACHE_CONFIG.VERSION,
        categoryCount: categories.length,
        source: "api",
        type: "popular-categories",
      };

      sessionStorage.setItem(
        CATEGORIES_CACHE_CONFIG.STORAGE_KEY,
        JSON.stringify(cacheData)
      );
      sessionStorage.setItem(
        CATEGORIES_CACHE_CONFIG.METADATA_KEY,
        JSON.stringify(metadata)
      );

      console.log("📂 Popular categories saved to sessionStorage:", {
        count: categories.length,
        size: `${(JSON.stringify(cacheData).length / 1024).toFixed(2)}KB`,
      });
    } catch (error) {
      console.warn("⚠️ Failed to save categories to sessionStorage:", error);
    }
  }, []);

  // Función para cargar categorías desde sessionStorage
  const loadCategoriesFromStorage = useCallback(() => {
    try {
      return null; // No usar caché por ahora, siempre cargar desde API

      const cachedData = sessionStorage.getItem(
        CATEGORIES_CACHE_CONFIG.STORAGE_KEY
      );
      const metadata = sessionStorage.getItem(
        CATEGORIES_CACHE_CONFIG.METADATA_KEY
      );

      if (!cachedData || !metadata) {
        console.log("📂 No popular categories cache found");
        return null;
      }

      const parsedData = JSON.parse(cachedData);
      const parsedMetadata = JSON.parse(metadata);

      // Validar versión del caché
      if (parsedData.version !== CACHE_CONFIG.VERSION) {
        console.log("🔄 Categories cache version mismatch, clearing");
        clearCategoriesStorage();
        return null;
      }

      // Validar TTL
      const now = Date.now();
      const cacheAge = now - parsedData.timestamp;

      if (cacheAge > CATEGORIES_CACHE_CONFIG.TTL) {
        console.log("⏰ Categories cache expired:", {
          age: `${Math.round(cacheAge / 1000 / 60)}min`,
          ttl: `${CATEGORIES_CACHE_CONFIG.TTL / 1000 / 60}min`,
        });
        clearCategoriesStorage();
        return null;
      }

      console.log("✅ Popular categories loaded from cache:", {
        count: parsedData.categories.length,
        age: `${Math.round(cacheAge / 1000 / 60)}min`,
      });

      return {
        categories: parsedData.categories,
        timestamp: parsedData.timestamp,
        metadata: parsedMetadata,
      };
    } catch (err) {
      console.warn("⚠️ Failed to load categories from cache:", err);
      clearCategoriesStorage();
      return null;
    }
  }, []);

  // Función para limpiar caché de categorías
  const clearCategoriesStorage = useCallback(() => {
    try {
      sessionStorage.removeItem(CATEGORIES_CACHE_CONFIG.STORAGE_KEY);
      sessionStorage.removeItem(CATEGORIES_CACHE_CONFIG.METADATA_KEY);
      console.log("🗑️ Popular categories cache cleared");
    } catch (err) {
      console.warn("⚠️ Failed to clear categories cache:", err);
    }
  }, []);

  // Función para cargar categorías desde la API
  const fetchPopularCategoriesFromAPI = useCallback(async () => {
    console.log("📂 Fetching popular categories from API...");

    try {
      // TODO: Cambiar por el endpoint real cuando esté disponible
      const response = await productsService.getPopularCategories({
        limit: CATEGORIES_CACHE_CONFIG.DEFAULT_LIMIT,
        area: 1,
      });

      if (response && response.data) {
        console.log("✅ Popular categories API fetch completed:", {
          categories: response.data.length,
          total: response.meta?.totalCategories || 0,
        });

        return response.data;
      } else {
        throw new Error("No popular categories received from API");
      }
    } catch (error) {
      console.error("❌ Error fetching popular categories:", error);

      // Fallback: generar categorías desde productos (temporal)
      return await generateCategoriesFromProducts();
    }
  }, []);

  // Función fallback temporal para generar categorías desde productos
  const generateCategoriesFromProducts = useCallback(async () => {
    try {
      console.log("📂 Generating categories from products (fallback)...");

      // Obtener productos para generar categorías
      const response = await productsService.getProducts({
        page: 1,
        limit: 100, // Obtener suficientes productos para análisis
        area: 1,
      });

      if (response && response.products) {
        // Contar productos por categoría
        const categoryCount = response.products.reduce((acc, product) => {
          if (product.category) {
            acc[product.category] = (acc[product.category] || 0) + 1;
          }
          return acc;
        }, {});

        // Convertir a formato esperado y ordenar por cantidad
        const categories = Object.entries(categoryCount)
          .sort(([, a], [, b]) => b - a)
          .slice(0, CATEGORIES_CACHE_CONFIG.DEFAULT_LIMIT)
          .map(([name, count], index) => ({
            id: (index + 1).toString(),
            name,
            slug: name.toLowerCase().replace(/\s+/g, "-"),
            productCount: count,
            percentage: ((count / response.products.length) * 100).toFixed(1),
            description: `Productos de la categoría ${name}`,
            image: `/images/categories/${name
              .toLowerCase()
              .replace(/\s+/g, "-")}.jpg`,
            featured: index < 3, // Primeras 3 como destacadas
            averagePrice: "0",
            priceRange: { min: "0", max: "0" },
            popularBrands: [],
            recentYears: [],
          }));

        console.log(
          "✅ Categories generated from products:",
          categories.length
        );
        return categories;
      }

      return [];
    } catch (error) {
      console.error("❌ Error generating categories from products:", error);
      return [];
    }
  }, []);

  // Cargar categorías populares
  const loadPopularCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Intentar cargar desde caché primero
      const cachedData = loadCategoriesFromStorage();

      if (cachedData && cachedData.categories?.length > 0) {
        setPopularCategories(cachedData.categories);
        setCacheSource("sessionStorage");
        setLoading(false);
        console.log("🚀 Popular categories loaded from cache");
        return;
      }

      // Si no hay caché válido, cargar desde API
      setCacheSource("api");
      const freshCategories = await fetchPopularCategoriesFromAPI();

      if (freshCategories.length > 0) {
        setPopularCategories(freshCategories);
        saveCategoriesStorage(freshCategories);
        console.log("✅ Popular categories loaded from API and cached");
      } else {
        throw new Error("No popular categories received");
      }
    } catch (err) {
      console.error("❌ Error loading popular categories:", err);
      setError(err.message);
      setCacheSource("none");
    } finally {
      setLoading(false);
    }
  }, [
    loadCategoriesFromStorage,
    fetchPopularCategoriesFromAPI,
    saveCategoriesStorage,
  ]);

  // Refrescar caché de categorías
  const refreshCategoriesCache = useCallback(async () => {
    console.log("🔄 Refreshing popular categories cache...");
    clearCategoriesStorage();
    setCacheSource("none");
    await loadPopularCategories();
  }, [clearCategoriesStorage, loadPopularCategories]);

  // Cargar categorías al montar
  useEffect(() => {
    loadPopularCategories();
  }, [loadPopularCategories]);

  return {
    popularCategories,
    loading,
    error,
    cacheSource,
    refreshCategoriesCache,
    clearCategoriesCache: clearCategoriesStorage,
    // Stats para debugging
    categoriesStats: {
      source: cacheSource,
      categoryCount: popularCategories.length,
      hasCache: !!sessionStorage.getItem(CATEGORIES_CACHE_CONFIG.STORAGE_KEY),
    },
  };
};

export default usePopularCategories;
